import { useState } from "react";
import useModal from "@/hooks/useModal";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import { useMutation } from "@apollo/client";
import { DELETE_STAGED_PARTICIPATION } from "../../../../../queries";
import { showSnackbar } from "@/lib/snackbarUtils";

export default function RemoveParticipationModal({
  onDismiss,
  selectedParticipantIds,
  onSuccess
}: {
  onDismiss: () => void;
  selectedParticipantIds: string[];
  onSuccess?: () => void | Promise<unknown>;
}) {
  const { Modal, close } = useModal();
  const [bannerError, setBannerError] = useState("");
  const count = selectedParticipantIds.length;

  const [deleteStagedParticipation, { loading }] = useMutation(DELETE_STAGED_PARTICIPATION, {
    onCompleted: async (data) => {
      const result = data?.deleteStagedParticipation;
      if (result?.success) {
        await onSuccess?.();
        showSnackbar(result.message ?? "Participants removed", { close: true });
        close();
        onDismiss();
      } else {
        setBannerError(result?.message ?? "Failed to remove participants.");
      }
    },
    onError: (error) => {
      setBannerError(error.message ?? "Failed to remove participants.");
    }
  });

  const handleRemove = async () => {
    setBannerError("");
    await deleteStagedParticipation({
      variables: {
        input: {
          participationIds: selectedParticipantIds
        }
      }
    });
  };

  return (
    <Modal
      onClosed={onDismiss}
      actions={
        <>
          <Button variant="text" onClick={close} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            onClick={handleRemove}
            disabled={loading}
            data-testid="remove-participation-confirm-button"
          >
            Remove
          </Button>
        </>
      }
      headline="Remove Participation"
      displayOverflow
      className="md:w-[520px]"
    >
      <div className="space-y-4">
        {!!bannerError && (
          <Banner inline variant="error" description={bannerError} closable onClose={() => setBannerError("")} />
        )}
        <p className="text-body-large text-sys-brand-on-surface">
          Are you sure you want to remove {count === 1 ? "this staged participant" : `${count} staged participants`}{" "}
          from this activity? This cannot be undone.
        </p>
      </div>
    </Modal>
  );
}
